import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ENV from 'constants/env';
import payrollnootLogoLg from 'assets/images/payrollnoot-logo-lg.png';
import './login.scss';

const API_BASE = (() => {
    const raw = ENV.BASE_URL || '';
    const trimmed = raw.trim().replace(/\/+$/, '');
    return trimmed.endsWith('/api') ? trimmed : `${trimmed}/api`;
})();

const inviteApi = axios.create({
    baseURL: API_BASE,
    headers: { 'X-API-key': ENV.API_KEY }
});

const RegisterFromInvite = () => {
    const { type } = useParams();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const navigate = useNavigate();

    const [invite, setInvite] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [form, setForm] = useState({ firstName: '', lastName: '', password: '', confirmPassword: '' });

    useEffect(() => {
        if (!token) { setError('Invalid invite link — no token found.'); setLoading(false); return; }
        inviteApi.get(`/invites/validate/${token}`)
            .then(res => { setInvite(res.data); setLoading(false); })
            .catch(err => { setError(err.response?.data?.message || 'Invalid or expired invite link.'); setLoading(false); });
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (form.password !== form.confirmPassword) return setError('Passwords do not match.');
        if (form.password.length < 8) return setError('Password must be at least 8 characters.');
        setSubmitting(true);
        try {
            await inviteApi.post(`/invites/complete`, {
                token, password: form.password,
                firstName: form.firstName, lastName: form.lastName,
            });
            setSuccess(true);
        } catch (err) {
            setError(err.response?.data?.message || 'Registration failed. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const loginPath = type === 'accountant' ? '/login/accountant' : '/login/company';
    const typeLabel = type === 'accountant' ? 'Accountant' : 'Company';

    if (loading) return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <p style={{ color: '#6b7280' }}>Validating invite link...</p>
        </div>
    );

    return (
        <div style={{ minHeight: '100vh', background: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', padding: '40px 36px', width: '100%', maxWidth: 440 }}>
                <div style={{ textAlign: 'center', marginBottom: 28 }}>
                    <img src={payrollnootLogoLg} alt="PayrollNoot" style={{ height: 48, marginBottom: 16 }} />
                    <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1a1a1a', margin: 0 }}>Complete Your Registration</h2>
                    {invite && <p style={{ color: '#6b7280', marginTop: 8, fontSize: 14 }}>Setting up your <strong>{typeLabel}</strong> account for <strong>{invite.email}</strong></p>}
                </div>

                {error && (
                    <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 16px', color: '#ef4444', fontSize: 14, marginBottom: 20, textAlign: 'center' }}>
                        {error}
                    </div>
                )}

                {success ? (
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
                        <h3 style={{ fontWeight: 700, color: '#1a1a1a' }}>Registration Complete!</h3>
                        <p style={{ color: '#6b7280', marginBottom: 24 }}>Your account has been created. You can now log in.</p>
                        <button onClick={() => navigate(loginPath)}
                            style={{ background: '#E7730A', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 32px', fontWeight: 700, fontSize: 15, cursor: 'pointer', width: '100%' }}>
                            Go to Login
                        </button>
                    </div>
                ) : invite ? (
                    <form onSubmit={handleSubmit} autoComplete="off">
                        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                            <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#374151' }}>First Name</label>
                                <input type="text" value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })}
                                    placeholder="John" required autoComplete="given-name"
                                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#374151' }}>Last Name</label>
                                <input type="text" value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })}
                                    placeholder="Smith" required autoComplete="family-name"
                                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                            </div>
                        </div>
                        <div style={{ marginBottom: 16 }}>
                            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#374151' }}>Email Address</label>
                            <input type="email" value={invite.email} disabled autoComplete="off"
                                style={{ width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, background: '#f9fafb', color: '#6b7280', boxSizing: 'border-box' }} />
                        </div>
                        <div style={{ marginBottom: 16 }}>
                            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#374151' }}>Password</label>
                            <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                                placeholder="Minimum 8 characters" required minLength={8} autoComplete="new-password"
                                style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                        </div>
                        <div style={{ marginBottom: 24 }}>
                            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#374151' }}>Confirm Password</label>
                            <input type="password" value={form.confirmPassword} onChange={e => setForm({ ...form, confirmPassword: e.target.value })}
                                placeholder="Re-enter password" required autoComplete="new-password"
                                style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                        </div>
                        <button type="submit" disabled={submitting}
                            style={{ width: '100%', background: '#E7730A', color: '#fff', border: 'none', borderRadius: 8, padding: '12px', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>
                            {submitting ? 'Creating Account...' : 'Create Account'}
                        </button>
                    </form>
                ) : null}
            </div>
        </div>
    );
};

export default RegisterFromInvite;
